import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const DevSecOpsDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: 'secure-sdlc',
      title: 'Secure SDLC Implementation',
      description: 'Embed security into every phase of your software development lifecycle. We help you shift security left, catching vulnerabilities early when they are cheapest to fix.',
      icon: ICONS.secureCode,
      features: [
        'Security Requirements Analysis',
        'Threat Modeling & Risk Assessment',
        'Secure Coding Standards',
        'Security Architecture Review',
        'Compliance Framework Mapping',
        'Security Training Programs'
      ]
    },
    {
      id: 'pipeline-security',
      title: 'CI/CD Pipeline Security',
      description: 'Integrate automated security testing into your CI/CD pipelines. We implement SAST, DAST, SCA, and container scanning to catch vulnerabilities before they reach production.',
      icon: ICONS.cicdPipeline,
      features: [
        'Static Application Security Testing (SAST)',
        'Dynamic Application Security Testing (DAST)',
        'Software Composition Analysis (SCA)',
        'Container Image Scanning',
        'Infrastructure as Code Security',
        'Secret Detection & Management'
      ]
    },
    {
      id: 'cloud-security',
      title: 'Cloud Security Posture',
      description: 'Secure your cloud infrastructure with continuous monitoring and compliance automation. We implement cloud security best practices across AWS, Azure, and GCP.',
      icon: ICONS.security,
      features: [
        'Cloud Security Assessment',
        'CSPM Implementation',
        'IAM Policy Optimization',
        'Network Security Architecture',
        'Data Encryption Strategy',
        'Compliance Automation (SOC2, ISO27001)'
      ]
    },
    {
      id: 'runtime-security',
      title: 'Runtime Security & Monitoring',
      description: 'Protect your applications in production with real-time threat detection and response. We implement defense-in-depth strategies to detect and respond to security incidents.',
      icon: ICONS.monitoring,
      features: [
        'Runtime Application Self-Protection (RASP)',
        'Security Information & Event Management',
        'Intrusion Detection Systems',
        'Incident Response Automation',
        'Security Orchestration (SOAR)',
        'Threat Intelligence Integration'
      ]
    }
  ]

  const securityTools = [
    { name: 'SonarQube', category: 'SAST' },
    { name: 'Checkmarx', category: 'SAST' },
    { name: 'OWASP ZAP', category: 'DAST' },
    { name: 'Snyk', category: 'SCA' },
    { name: 'Trivy', category: 'Container' },
    { name: 'Vault', category: 'Secrets' },
    { name: 'Falco', category: 'Runtime' },
    { name: 'Prowler', category: 'Cloud' },
    { name: 'Checkov', category: 'IaC' },
    { name: 'GitLeaks', category: 'Secrets' }
  ]

  const complianceFrameworks = [
    { name: 'SOC 2 Type II', description: 'Security, availability, and confidentiality controls for service organizations.' },
    { name: 'ISO 27001', description: 'International standard for information security management systems.' },
    { name: 'PCI DSS', description: 'Payment card industry data security standards for handling cardholder data.' },
    { name: 'NESA', description: 'UAE National Electronic Security Authority standards for critical infrastructure.' },
    { name: 'GDPR', description: 'European data protection regulation for handling personal data.' },
    { name: 'HIPAA', description: 'US healthcare data protection requirements for medical information.' }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const faqs = [
    {
      question: 'What is DevSecOps and how is it different from DevOps?',
      answer: 'DevSecOps integrates security practices into every stage of the software development lifecycle, from code commit to production deployment. While DevOps focuses on speed and automation of software delivery, DevSecOps adds automated security testing including SAST, DAST, container scanning, and infrastructure security auditing. This shift-left approach catches vulnerabilities early when they are cheapest to fix, rather than treating security as a separate phase at the end.'
    },
    {
      question: 'How much do DevSecOps services cost in Dubai?',
      answer: 'DevSecOps consulting in Dubai typically ranges from AED 600 to AED 1,800 per hour depending on the complexity and expertise required. A basic CI/CD security integration project starts from AED 30,000, while a comprehensive DevSecOps transformation including secure SDLC implementation, compliance automation, and runtime security ranges from AED 80,000 to AED 300,000. We offer fixed-price, time-and-materials, and monthly retainer engagement models.'
    },
    {
      question: 'How long does it take to implement DevSecOps?',
      answer: 'A basic DevSecOps implementation integrating security scanning into existing CI/CD pipelines takes 2 to 4 weeks. A full DevSecOps transformation including secure SDLC adoption, compliance automation, cloud security posture management, and team training typically takes 8 to 16 weeks. We use a phased approach so you see security improvements from the first sprint.'
    },
    {
      question: 'Which security tools do you integrate into CI/CD pipelines?',
      answer: 'We integrate industry-leading security tools including SonarQube and Checkmarx for static analysis (SAST), OWASP ZAP for dynamic testing (DAST), Snyk for software composition analysis (SCA), Trivy for container image scanning, HashiCorp Vault for secrets management, Checkov for infrastructure as code security, and GitLeaks for secret detection. We select tools based on your existing stack and compliance requirements.'
    },
    {
      question: 'Can you help with SOC 2 and ISO 27001 compliance in Dubai?',
      answer: 'Yes, compliance automation is a core part of our DevSecOps services. We help Dubai businesses achieve and maintain SOC 2 Type II, ISO 27001, PCI DSS, NESA, GDPR, and HIPAA compliance through automated security controls, continuous monitoring, evidence collection, and audit-ready reporting. We implement compliance as code so your security posture is continuously validated rather than checked only during annual audits.'
    },
    {
      question: 'What industries benefit most from DevSecOps in Dubai?',
      answer: 'Industries handling sensitive data benefit most from DevSecOps in Dubai including fintech and banking (PCI DSS compliance), healthcare and medtech (HIPAA), government and public sector (NESA), e-commerce (payment security), and SaaS companies serving enterprise clients (SOC 2). Any organization developing software that processes personal data or financial information should consider DevSecOps practices.'
    },
    {
      question: 'Do you provide security training for development teams?',
      answer: 'Yes, security training and culture building is an essential part of our DevSecOps services. We provide secure coding workshops, threat modeling training, security champion programs, and hands-on labs tailored to your tech stack. Our goal is to make your development team security-aware and self-sufficient in identifying and remediating common vulnerabilities.'
    }
  ]

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'DevSecOps Dubai', path: '/devsecops-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="DevSecOps Services Dubai | Secure Software Development & SSDLC UAE"
        description="Expert DevSecOps services in Dubai. We integrate security into your CI/CD pipelines with SAST, DAST, container scanning, and cloud security. Secure SDLC consulting for UAE businesses."
        keywords={[
          'devsecops dubai',
          'devsecops services uae',
          'secure sdlc dubai',
          'application security dubai',
          'sast dast dubai',
          'secure software development dubai',
          'security automation uae',
          'cloud security dubai',
          'container security uae',
          'soc2 compliance dubai',
          'iso 27001 consulting uae',
          'penetration testing dubai'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              Security + DevOps
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              DevSecOps Services in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Build security into your software from day one. We help Dubai businesses implement
              DevSecOps practices that automate security testing, ensure compliance, and protect
              against modern threats without slowing down development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule Security Assessment
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { stat: '90%', label: 'Vulnerability Reduction' },
              { stat: '100x', label: 'Faster Remediation' },
              { stat: '85%', label: 'Cost Savings vs Late Detection' },
              { stat: '0', label: 'Production Breaches' }
            ].map((item, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{item.stat}</div>
                <div className="text-primary-100">{item.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our DevSecOps Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                End-to-end security integration for your software development and deployment
                processes, from code to cloud.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <Icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Security Tools Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Security Tools We Implement
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Industry-leading security tools integrated into your development workflow.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {securityTools.map((tool, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{tool.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{tool.category}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Compliance Frameworks Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Compliance Frameworks We Support
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We help you achieve and maintain compliance with industry standards and regulations.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {complianceFrameworks.map((framework, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {framework.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {framework.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about DevSecOps services in Dubai answered by our security engineering team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/devops-services-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevOps Services
              </Link>
              <Link
                to="/kubernetes-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Kubernetes Consulting
              </Link>
              <Link
                to="/aws-cloud-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                AWS Cloud Consulting
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Secure Your Development Pipeline?"
        description="Let's discuss how DevSecOps can help your Dubai business build more secure software while maintaining development velocity."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default DevSecOpsDubai
